import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import { Cursor, useTypewriter } from 'react-simple-typewriter'
import { IKImage } from 'imagekitio-react';
import { useTheme } from '../contexts/ThemeContext';
import { MyContext } from '../contexts/MyContext';

const Banner = () => {
    const { isDark } = useTheme();
    const { bannerImgLink } = useContext(MyContext);

    const [text] = useTypewriter({
        words: ['BUILD YOUR WEBSITE?', 'CREATE YOUR SOFTWARE?', 'AUTOMATE YOUR PROCESS?'],
        loop: true,
        typeSpeed: 30,
        deleteSpeed: 15,
        delaySpeed: 2000
    });

    return (
        <div className='min-h-[70vh] md:min-h-[80vh] flex flex-col-reverse md:flex-row md:gap-8 lg:gap-12 items-center py-8 md:py-0'>
            {/* Content Section */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className='flex-1 w-full md:w-auto mt-8 md:mt-0'
            >
                {/* Terminal-style prompt */}
                <div className={`mb-4 font-mono text-sm md:text-base ${
                    isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                }`}>
                    <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
                        $
                    </span>
                    {' '}looking_for{' '}
                    <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                        --help
                    </span>
                </div>

                {/* Typewriter text */}
                <div className='min-h-[2.5rem] md:min-h-[3rem] mb-6'>
                    <span className={`text-base md:text-lg lg:text-xl font-mono ${
                        isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'
                    }`}>
                        {text}
                    </span>
                    <Cursor
                        cursorStyle='_'
                        cursorColor={isDark ? '#00ff41' : '#0969da'}
                    />
                </div>

                {/* Main heading */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                >
                    <h1 className={`text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 font-mono ${
                        isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                    }`}>
                        <span className={`block mb-2 ${
                            isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'
                        }`}>
                            {'>'} Hi there,
                        </span>
                        <span className={isDark ? 'text-dark-text-primary' : 'text-light-text-primary'}>
                            I'm <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
                                Zunayed
                            </span>
                        </span>
                    </h1>
                </motion.div>

                {/* Role/Title */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className={`text-lg md:text-xl lg:text-2xl font-mono mb-8 ${
                        isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                    }`}
                >
                    <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                        const
                    </span>
                    {' role = '}
                    <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
                        "Software Developer"
                    </span>
                </motion.div>

                {/* CTA Button */}
                <Link to="/hireme">
                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        className={`font-mono font-medium rounded-lg text-sm md:text-base px-6 py-3 transition-all border-2 ${
                            isDark
                                ? 'bg-dark-brand-primary text-dark-bg-primary border-dark-brand-primary hover:bg-transparent hover:text-dark-brand-primary shadow-glow-sm hover:shadow-glow-md'
                                : 'bg-light-brand-primary text-white border-light-brand-primary hover:bg-transparent hover:text-light-brand-primary shadow-light-glow-sm hover:shadow-light-glow-md'
                        }`}>
                        $ get_in_touch
                    </motion.button>
                </Link>
            </motion.div>

            {/* Profile Image Section */}
            <motion.div
                className='flex-1 w-full md:w-auto max-w-md lg:max-w-lg flex items-center justify-center relative'
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
            >
                <div className="relative w-full">
                    {bannerImgLink && (
                        <>
                            <IKImage
                                path={bannerImgLink}
                                transformation={[{
                                    width: 500,
                                    height: 500,
                                    quality: 85
                                }]}
                                className="w-full h-auto object-cover opacity-80"
                                alt="Zunayed Fahim"
                                loading="lazy"
                            />
                            {/* Bottom gradient blur overlay */}
                            <div className={`absolute bottom-0 left-0 right-0 pointer-events-none ${
                                isDark
                                    ? 'h-1/3 bg-gradient-to-t from-dark-bg-primary via-dark-bg-primary/60 to-transparent'
                                    : 'h-1/4 bg-gradient-to-t from-light-bg-primary via-light-bg-primary/40 to-transparent'
                            }`}></div>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    )
}

export default Banner
